import { type ReactNode } from 'react';
import { XCircle } from 'lucide-react';
import { Button } from './button';
import { PaginationBar, type PaginationBarProps } from './pagination-bar';
import { cn } from '@/lib/utils';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  rowClassName?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  loadingRows?: number;
  error?: boolean;
  onRetry?: () => void;
  empty?: ReactNode;
  pagination?: PaginationBarProps;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowClassName,
  onRowClick,
  loading = false,
  loadingRows = 8,
  error = false,
  onRetry,
  empty,
  pagination,
}: DataTableProps<T>) {
  return (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-primary text-xs uppercase tracking-wider text-primary-foreground">
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={cn('px-4 py-2.5 text-left font-semibold', col.className)}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {loading ? (
              Array.from({ length: loadingRows }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {columns.map((col) => (
                    <td key={col.key} className={cn('px-4 py-3', col.className)}>
                      <div className="h-4 rounded bg-muted/60" />
                    </td>
                  ))}
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center">
                  <div className="flex flex-col items-center gap-2 text-destructive">
                    <XCircle className="h-8 w-8 opacity-60" />
                    <span className="text-sm font-medium">Failed to load data</span>
                    {onRetry && (
                      <Button size="sm" variant="outline" onClick={onRetry}>
                        Retry
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">
                  {empty ?? 'No data'}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={() => onRowClick?.(row)}
                  className={cn(onRowClick ? 'cursor-pointer' : '', 'hover:bg-muted/30', rowClassName?.(row))}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={cn('px-4 py-2 align-middle', col.className)}>
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && <PaginationBar {...pagination} />}
    </div>
  );
}
